import type { ArrivalData } from 'common/src/api/shipment-arrivals';
import type { ShipmentTrackingSummary } from 'common/src/utils/types';
import {
  getShipmentCalendarDayOffset,
  getShipmentLogisticsStatus,
} from '../../../utils/shipment-logistics';
import { getLocalEventDay } from '../../../utils/tracking-local-time';
import {
  countryCodeToFlag,
  getTrackingEventLabel,
  trackingCountryCode,
} from '../../../utils/shipment-tracking-display';
import { normalizeTransport } from '../../../utils/client-reports/build-shipments-section';

export type ArrivalGroupKey = 'pod' | 'cliente' | 'fecha';
export type ArrivalSubGroupKey = ArrivalGroupKey | 'none';
export type TimelineMetric = 'containers' | 'kg';
export interface ArrivalMixLine {
  variety: string;
  size: string;
  color: string;
  format: string;
  boxWeight: number;
  label: string;
  producer: string;
  kg: number;
  quantity: number;
  ageDays: number | null;
}
export type ArrivalMode = 'ocean' | 'air';
export interface Arrival {
  id: string;
  shipmentId: number;
  mode: ArrivalMode;
  containerKey: string;
  container: string;
  clientKey: string;
  client: string;
  reference: string;
  pod: string;
  portName: string;
  flag: string;
  vessel: string;
  voyage: string;
  milestone: string;
  arrived: boolean;
  eta: string | null;
  /** Departure (tracking or planned), to estimate the position when there is no live one. */
  etd: string | null;
  etaSource: 'actual' | 'tracking' | 'planned' | null;
  plannedEtd: string | null;
  plannedEta: string | null;
  offsetDays: number | null;
  urgent: boolean;
  kg: number;
  quantity: number;
  delayDays: number | null;
  journey: ShipmentTrackingSummary | null;
  mix: ArrivalMixLine[];
}
const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';
const missing = (value: unknown) => text(value) || 'Sin dato';
const identifier = (value: unknown) =>
  text(value).toUpperCase().replace(/[\s-]/g, '');
const validDate = (value: unknown, today: Date) =>
  text(value) && getShipmentCalendarDayOffset(text(value), today) !== null
    ? text(value)
    : null;
const number = (value: unknown) =>
  Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0;
const eventLabels: Record<string, string> = {
  EMSH: 'Contenedor entregado',
  GTIN: 'Ingreso a terminal',
  STUF: 'Consolidado',
  LOAD: 'Cargado en nave',
  DEPA: 'Nave zarpó',
  ARRV: 'Arribo a puerto',
  ARRIVED: 'Arribo a puerto',
  DISC: 'Descargado',
  GTOT: 'Salida de terminal',
  STRP: 'Desconsolidado',
  EMPTY_RETURN: 'Contenedor devuelto',
};

export function canRefreshArrival(journey: ShipmentTrackingSummary | null) {
  return Boolean(
    journey?.provider_tracking_id &&
      ['active', 'completed'].includes(journey.status) &&
      !['container_returned', 'closed'].includes(
        journey.observation_status ?? ''
      )
  );
}
export const containerCount = (arrivals: Arrival[]) =>
  new Set(arrivals.map((arrival) => arrival.containerKey)).size;
/** "3 contenedores", or "3 contenedores · 2 envíos aéreos" when air is present. */
export function unitLabel(arrivals: Arrival[]) {
  const sea = containerCount(arrivals.filter((a) => a.mode === 'ocean'));
  const air = containerCount(arrivals.filter((a) => a.mode === 'air'));
  const parts: string[] = [];
  if (sea || !air)
    parts.push(`${sea} ${sea === 1 ? 'contenedor' : 'contenedores'}`);
  if (air) parts.push(`${air} ${air === 1 ? 'envío aéreo' : 'envíos aéreos'}`);
  return parts.join(' · ');
}
const awbDigits = (value: unknown) => text(value).replace(/\D/g, '');
const formatAwb = (digits: string) =>
  digits.length === 11 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits;
// ship_type is free text (M, MARITIMO, SEA, AÉREO, AIR…). Without a usable
// value, a container number means sea and an AWB alone means air.
function arrivalMode(box: ArrivalData['cargo'][number]): ArrivalMode | null {
  const transport = normalizeTransport(box.ship_type);
  if (transport === 'M') return 'ocean';
  if (transport === 'A') return 'air';
  if (transport === 'T') return null;
  if (identifier(box.container_number)) return 'ocean';
  if (awbDigits(box.awb_number)) return 'air';
  return null;
}
export function fruitAge(arrivals: Arrival[]) {
  let kg = 0,
    weighted = 0,
    total = 0;
  for (const arrival of arrivals)
    for (const line of arrival.mix) {
      total += line.kg;
      if (line.ageDays !== null) {
        kg += line.kg;
        weighted += line.kg * line.ageDays;
      }
    }
  return {
    days: kg > 0 ? weighted / kg : null,
    knownKg: kg,
    missingKg: total - kg,
    totalKg: total,
  };
}
export function ageBucket(days: number | null) {
  if (days === null) return 'Sin fecha de embalaje';
  if (days <= 25) return '≤ 25 días';
  if (days <= 35) return '26–35 días';
  if (days <= 45) return '36–45 días';
  return '> 45 días';
}

export function buildArrivals(data: ArrivalData, today: Date): Arrival[] {
  const groups = new Map<string, Arrival>();
  for (const box of data.cargo) {
    const mode = arrivalMode(box);
    if (!mode) continue;
    const shipmentId = Number(box.shipment_id);
    if (!Number.isSafeInteger(shipmentId) || shipmentId <= 0)
      throw new Error('Despacho de arribo sin ID válido');
    const awb = awbDigits(box.awb_number);
    // Sea cargo needs its container; air cargo is shown even without an AWB.
    const container =
      mode === 'ocean'
        ? identifier(box.container_number)
        : awb
          ? formatAwb(awb)
          : 'Sin AWB';
    if (mode === 'ocean' && !container) continue;
    const referenceKey = text(box.reference_number) || '__SIN_REFERENCIA__';
    const candidates = (data.tracking[referenceKey] ?? []).filter(
      (candidate) =>
        candidate.mode === mode &&
        !['cancelled', 'rejected', 'window_expired'].includes(candidate.status)
    );
    // Only the journey matching this physical container/AWB can supply its
    // ETA. An air shipment without AWB can use its reference's only air
    // journey, since the AWB was then found elsewhere (e.g. its reference).
    const journey =
      candidates.find((candidate) =>
        mode === 'ocean'
          ? identifier(candidate.identifier_value) === container
          : awb && awbDigits(candidate.identifier_value) === awb
      ) ??
      (mode === 'air' && !awb && candidates.length === 1
        ? candidates[0]
        : null);
    // Arrival day as lived at the destination port/airport, not in UTC.
    const liveEta = validDate(
      getLocalEventDay(journey?.live_eta, journey?.destination_timezone),
      today
    );
    const plannedEta = validDate(box.ship_eta, today);
    const arrived = journey
      ? getShipmentLogisticsStatus(
          {
            ship_eta: box.ship_eta,
            ship_etd: box.ship_etd,
            ship_destination_port: box.ship_destination_port,
            tracking_journeys: [journey],
          },
          today
        ) === 'arrived'
      : false;
    // Only a destination-matched arrival is proof of the actual arrival date.
    // Lifecycle projections can describe a transshipment port.
    const actualArrival =
      arrived &&
      ['ARRV', 'ARR'].includes(
        journey?.latest_actual_event?.event_type.toUpperCase() ?? ''
      )
        ? validDate(journey.latest_actual_event.occurred_at, today)
        : null;
    const eta = actualArrival || liveEta || plannedEta;
    const offsetDays = eta ? getShipmentCalendarDayOffset(eta, today) : null;
    const locode = identifier(journey?.destination_locode);
    const portName =
      text(journey?.destination_name) ||
      text(box.ship_destination_port) ||
      (mode === 'air' ? 'Sin aeropuerto de destino' : 'Sin puerto de destino');
    const pod = locode || portName.toUpperCase();
    const countryCode =
      trackingCountryCode({
        countryCode: journey?.destination_country_code,
        locode,
        fallbackCountry: box.ship_destination_country,
      }) ?? '';
    const flag = countryCode ? countryCodeToFlag(countryCode) : '';
    const containerKey =
      journey?.id ??
      JSON.stringify([
        mode === 'air' && !awb ? `${mode}:${shipmentId}` : container,
        box.ship_etd || box.shipment_id,
        box.ship_name,
      ]);
    const clientKey =
      box.exporter_receiver_id != null
        ? String(box.exporter_receiver_id)
        : missing(box.receiver).toUpperCase();
    // A shared container can have cargo for several clients. Allocate kilos to
    // each client, while counting its physical arrival only once in totals.
    const key = JSON.stringify([shipmentId, containerKey, clientKey, pod]);
    let arrival = groups.get(key);
    if (!arrival) {
      const event = journey?.latest_actual_event;
      const eventType = event?.event_type.toUpperCase();
      const urgent =
        !arrived && offsetDays !== null && offsetDays >= 0 && offsetDays <= 1;
      arrival = {
        id: key,
        shipmentId,
        mode,
        containerKey,
        container,
        clientKey,
        client: missing(box.receiver),
        reference: text(box.reference_number) || 'Sin referencia',
        pod,
        portName,
        flag,
        vessel:
          mode === 'air'
            ? text(journey?.carrier_name) || 'Sin aerolínea'
            : text(event?.vessel_name) ||
              text(journey?.latest_estimated_event?.vessel_name) ||
              text(box.ship_name) ||
              'Sin nave',
        voyage:
          text(event?.voyage_number) ||
          text(journey?.latest_estimated_event?.voyage_number) ||
          '—',
        milestone: arrived
          ? 'Arribo confirmado'
          : eventType
            ? ['ARRV', 'ARRIVED', 'DISC', 'ARR', 'RCF'].includes(eventType)
              ? 'En tránsito · escala'
              : eventLabels[eventType] ||
                text(event?.description) ||
                getTrackingEventLabel(eventType)
            : journey
              ? 'Sin hito confirmado'
              : 'Sin tracking',
        arrived,
        eta,
        etd:
          validDate(getLocalEventDay(journey?.live_etd, null), today) ||
          validDate(box.ship_etd, today),
        etaSource: actualArrival
          ? 'actual'
          : liveEta
            ? 'tracking'
            : plannedEta
              ? 'planned'
              : null,
        plannedEtd: validDate(box.ship_etd, today),
        plannedEta,
        offsetDays,
        urgent,
        kg: 0,
        quantity: 0,
        journey,
        delayDays:
          liveEta && plannedEta
            ? Math.max(
                0,
                getShipmentCalendarDayOffset(liveEta, today)! -
                  getShipmentCalendarDayOffset(plannedEta, today)!
              )
            : null,
        mix: [],
      };
      groups.set(key, arrival);
    }
    const quantity = number(box.quantity);
    const weight = number(box.box_weight);
    const kg = quantity * weight;
    // Age counts from packing (Fecha Proceso); the packing dispatch date is
    // the fallback. Few exporters load the reception date.
    const packed =
      validDate(box.process_date, today) ?? validDate(box.packing_etd, today);
    const packedOffset = packed
      ? getShipmentCalendarDayOffset(packed, today)
      : null;
    const age =
      offsetDays !== null && packedOffset !== null && packedOffset <= offsetDays
        ? offsetDays - packedOffset
        : null;
    arrival.kg += kg;
    arrival.quantity += quantity;
    arrival.mix.push({
      variety: missing(box.variety),
      size: missing(box.caliber),
      color: missing(box.color || box.category),
      format: missing(box.box_format),
      boxWeight: weight,
      label: missing(box.box_label),
      producer: missing(box.producer_name),
      kg,
      quantity,
      ageDays: age,
    });
  }
  return [...groups.values()].sort(
    (a, b) =>
      (a.offsetDays ?? Infinity) - (b.offsetDays ?? Infinity) ||
      a.container.localeCompare(b.container)
  );
}

export function arrivalWindow(arrivals: Arrival[], horizon: number) {
  return arrivals.filter(
    (arrival) =>
      arrival.offsetDays !== null &&
      arrival.offsetDays >= 0 &&
      arrival.offsetDays < horizon &&
      (!arrival.arrived || arrival.offsetDays === 0)
  );
}
export function overdueArrivals(arrivals: Arrival[]) {
  return arrivals.filter(
    (arrival) =>
      arrival.offsetDays !== null && arrival.offsetDays < 0 && !arrival.arrived
  );
}
